import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { Editor } from '@tiptap/react'
import { useEffect, useState } from 'react'

interface LinkDialogProps {
  editor: Editor
  open: boolean
  onOpenChange: (open: boolean) => void
}

function normalizeUrl(url: string): string {
  const trimmed = url.trim()

  if (!/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`
  }

  return trimmed
}

export function LinkDialog({ editor, open, onOpenChange }: LinkDialogProps) {
  const [url, setUrl] = useState('')
  const [text, setText] = useState('')

  const isEditing = editor.isActive('link')

  useEffect(() => {
    if (open) {
      if (isEditing) {
        setUrl(editor.getAttributes('link').href ?? '')
      } else {
        setUrl('')
      }
      const { from, to } = editor.state.selection
      const selectedText = editor.state.doc.textBetween(from, to, '')
      setText(selectedText)
    }
  }, [open, editor, isEditing])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!url.trim()) return

    const href = normalizeUrl(url)

    if (text && !editor.state.selection.empty) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href })
        .run()
    } else if (text) {
      editor
        .chain()
        .focus()
        .insertContent(`<a href="${href}">${text}</a>`)
        .run()
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href })
        .run()
    }

    onOpenChange(false)
  }

  const handleRemoveLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Link' : 'Insert Link'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="link-url">URL</Label>
            <Input
              id="link-url"
              data-testid="link-dialog-url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="link-text">Display Text</Label>
            <Input
              id="link-text"
              data-testid="link-dialog-text"
              placeholder="Link text"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <DialogFooter>
            {isEditing && (
              <Button type="button" variant="destructive" onClick={handleRemoveLink}>
                Remove Link
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {isEditing ? 'Update' : 'Insert'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
